/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointUrlsComponent" />
import * as React from "react";

import * as Constants from "DistributedTask/Common/AzureResourcesService/Constants";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { AzureEndpointConstants, EndpointAuthorizationSchemes, ServiceEndpointTypes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import {
    DataSourceDetails,
    DependencyData,
    ResultTransformationDetails,
    ServiceEndpoint,
    ServiceEndpointDetails,
    ServiceEndpointRequest,
    ServiceEndpointType
} from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { Link } from "VSSUI/Link";

import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { equals, isGuid } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";

export interface IServiceEndpointUrlsComponentProps {
    serviceEndpoint: ServiceEndpoint;
    serviceEndpointType: ServiceEndpointType;
}

export class ServiceEndpointUrlsComponent extends VssComponent<IServiceEndpointUrlsComponentProps>{
    constructor(props: IServiceEndpointUrlsComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._actionCreator = ActionCreatorManager.GetActionCreator<ServiceEndpointActionsCreator>(ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator");
        this._shouldDisplayEndpointLinks = this.getShouldDisplayEndpointLinks(this.props.serviceEndpoint);
    }

    public componentDidMount(): void {
        if (this._shouldDisplayEndpointLinks) {
            this.getManageServicePrincipalUrl().then(url => this._manageServicePrincipalUrl.value = url);
            this.getManageConnectionRolesUrl().then(url => this._manageConnectionRolesUrl.value = url);
        }
    }

    public render(): React.ReactNode {
        return (
            <span>
                {
                    this._shouldDisplayEndpointLinks &&
                    this.showAzureRMLinks()
                }
            </span>
        );
    }

    private showAzureRMLinks(): JSX.Element {
        return <React.Fragment>
            {
                <span className="sc-rmazure-links">
                    <span className="sc-rmazure-link">
                        {this.showObservableLink(this._manageConnectionRolesUrl, Resources.ManageServiceConnectionRoles)}
                    </span>
                    <span className="sc-rmazure-link">
                        {this.showObservableLink(this._manageServicePrincipalUrl, Resources.ManageServicePrincipal)}
                    </span>
                </span>
            }
        </React.Fragment>;
    }

    private getShouldDisplayEndpointLinks(endpoint: ServiceEndpoint): boolean {
        if (!equals(endpoint.type, ServiceEndpointTypes.AzureRM, true)) {
            return false;
        }

        const data = endpoint.data;
        if (equals(data[AzureEndpointConstants.EnvironmentString], AzureEndpointConstants.AzureStackString, true)) {
            return false;
        }

        if (equals(data[AzureEndpointConstants.IsDraft], "true", true)) {
            return false;
        }

        const scheme = endpoint.authorization.scheme;
        if (equals(scheme, EndpointAuthorizationSchemes.ManagedServiceIdentity, true)) {
            return false;
        }

        if (equals(scheme, EndpointAuthorizationSchemes.PublishProfile, true)) {
            return false;
        }

        return true;
    }

    private showObservableLink(url: IObservableValue<string>, label: string): JSX.Element {
        return <Observer url={url}>
            {(props: { url: string }) => {
                return (<Link
                    className="body-s"
                    href={props.url}
                    disabled={!props.url}
                    target="_blank"
                    ref="nofollow noopener">
                    {label}
                </Link>);
            }}
        </Observer>;
    }

    private createCaseInsensitiveKeyMapDict(objectDict: any) {
        let objectDictKeys = Object.getOwnPropertyNames(objectDict);
        let caseInsensitiveKeyMapDict: any = {};
        objectDictKeys.forEach((key) => {
            caseInsensitiveKeyMapDict[key.toLowerCase()] = key;
        });

        return caseInsensitiveKeyMapDict;
    }

    private async getManageServicePrincipalUrl(): Promise<string> {
        const endpoint = this.props.serviceEndpoint;
        const applicationId = endpoint.authorization.parameters[AzureEndpointConstants.SpnClientId];
        const tenantId = endpoint.authorization.parameters[AzureEndpointConstants.TenantId];
        let managementPortalUrl = this.getPortalUrl(endpoint, this.props.serviceEndpointType.dependencyData);
        const managedIdentityId = await this.getManagedIdentityId(applicationId);
        if (managedIdentityId) {
            return `${managementPortalUrl}#@${tenantId}/resource${managedIdentityId}`;
        }
        if (isGuid(tenantId)) {
            managementPortalUrl += `${tenantId}/`;
        }
        if (isGuid(applicationId)) {
            // AAD application URL is of form ${azurePortalUrl}/tenantId/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Overview/appId/${ApplicationId}
            return `${managementPortalUrl}#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Overview/appId/${applicationId}`;
        }
        // applicationId is invalid. Redirect to App Registrations page
        return `${managementPortalUrl}#blade/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/RegisteredApps`;
    }

    private async getManagedIdentityId(applicationId: string): Promise<string | undefined> {
        const query = {
            query: `resources | where properties.clientId =~ '${ applicationId }' and type =~ 'microsoft.managedidentity/userassignedidentities' | project id | take 1`
        };
        const dataSourceDetails = {
            dataSourceUrl: "{{endpoint.url}}/providers/Microsoft.ResourceGraph/resources?api-version=2021-03-01",
            requestContent: JSON.stringify(query),
            requestVerb: "POST",
            resultSelector: "jsonpath:$.data[*].id"
        } as DataSourceDetails;
        const serviceEndpointRequest: ServiceEndpointRequest = {
            dataSourceDetails: dataSourceDetails,
            resultTransformationDetails: {} as ResultTransformationDetails,
            serviceEndpointDetails: this.getEmptyEndpointDetails(this.props.serviceEndpoint)
        };

        const response = await this._actionCreator.executeServiceEndpointRequest(serviceEndpointRequest, "0");
        if (!response || !response.result || !response.statusCode || response.statusCode.toString() !== "200") {
            return undefined;
        }
        const ids = response.result as string[];
        return (ids && ids.length === 1) ? ids[0] : undefined;
    }

    private getEmptyEndpointDetails(endpoint: ServiceEndpoint): ServiceEndpointDetails {
        const serviceEndpointDetails: ServiceEndpointDetails = {
            authorization: {
                parameters: {
                    accessTokenType: Constants.AppToken,
                    tenantid: endpoint.authorization.parameters.tenantid
                },
                scheme: Constants.EndpointAuthorizationSchemes.ServicePrincipal
            },
            data: {
                environment: endpoint.data.environment
            },
            type: Constants.Azurerm,
            url: endpoint.url
        };

        return serviceEndpointDetails;
    }

    private async getManageConnectionRolesUrl(): Promise<string> {
        const endpoint = this.props.serviceEndpoint;
        const armManagementPortalUrl = this.getPortalUrl(endpoint, this.props.serviceEndpointType.dependencyData);
        const selectedScope = endpoint.data[AzureEndpointConstants.ScopeLevel];
        const authParams = endpoint.authorization.parameters;
        const tenantId = authParams[AzureEndpointConstants.TenantId];

        let navigationUrl = armManagementPortalUrl + tenantId;
        if (selectedScope === AzureEndpointConstants.SubscriptionString) {
            const subscriptionId = endpoint.data[AzureEndpointConstants.SubScriptionId];
            const authParamKeyMapDict = this.createCaseInsensitiveKeyMapDict(authParams);

            // IAM url is of form {portalUrl}/{tenantId}/?resourceMenuPerf=true#resource/{resourceId}/users
            const authorizationScope = authParams[authParamKeyMapDict["scope"]] || `/subscriptions/${subscriptionId}`;
            navigationUrl += "?resourceMenuPerf=true#resource" + authorizationScope + "/users";
        }
        else if (selectedScope === AzureEndpointConstants.ManagementGroupString) {
            const managementGroupId = endpoint.data[AzureEndpointConstants.AzureManagementGroupId];
            navigationUrl += "/#blade/Microsoft_Azure_ManagementGroups/MenuBlade/iam/name/" + managementGroupId;
        }

        return await Promise.resolve(navigationUrl);
    }

    private getPortalUrl(endpoint: ServiceEndpoint, dependencyData: DependencyData[]): string {
        const defaultPortalUrl = "https://portal.azure.com/";

        if (!dependencyData) {
            return defaultPortalUrl;
        }

        const environmentName = endpoint.data[AzureEndpointConstants.EnvironmentString] || AzureEndpointConstants.DefaultAzureEnvironment;
        const environment = dependencyData
            .filter(d => equals(d.input, AzureEndpointConstants.EnvironmentString, true))
            .flatMap(d => d.map)
            .find(e => equals(e.key, environmentName, true));

        if (!environment) {
            return defaultPortalUrl;
        }

        const url = environment.value.find(v => equals(v.key, AzureEndpointConstants.DefaultArmManagementPortalUriString, true));
        if (!url || !url.value) {
            return defaultPortalUrl;
        }

        return url.value;
    }

    private readonly _actionCreator: ServiceEndpointActionsCreator;
    private readonly _manageServicePrincipalUrl: IObservableValue<string> = new ObservableValue("");
    private readonly _manageConnectionRolesUrl: IObservableValue<string> = new ObservableValue("");
    private readonly _shouldDisplayEndpointLinks: boolean = true;
}


